import { EnqueueSnackbar } from '@automata/rexports/notistack';
import { logger } from '@automata/utils';
import { WorkflowConfigInput } from '@automata/wfb-api';
import {
  OrgScriptsInfo,
  ParameterDefinition,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { createAction } from '@reduxjs/toolkit';
import willCreateCycle from 'graphology-dag/will-create-cycle';
import startCase from 'lodash/startCase';
import { Connection, Edge } from 'reactflow';

import {
  getDeleteConflictEdgeIds,
  getLabwareId,
  getSameOriginEdge,
  getSameTargetsWithDifferentLabware,
  handleCutCopyData,
} from './helpers';
import type { AppThunk } from '../../store';
import {
  CodeTaskProperties,
  ConditionalTaskProperties,
  ContinuousLoadingProperties,
  CreateLabwareData,
  CreateTaskData,
  DataExportTaskProperties,
  GraphEdges,
  LabwareProperties,
  LabwareType,
  PropertiesType,
  TaskProperties,
  TaskType,
  TimeConstraintInstance,
} from '../../types';
import {
  addPositions,
  defaultLabwareProperties,
  determineNewNodePosition,
  isStartOrStopShakeTask,
} from '../../utils';
import {
  getInitialDataModesForTaskType,
  isFileUploadEnabled,
} from '../../utils/dataModes';
import { DataMode } from '../../zodSchemas/taskProperties';
import { CutData, selectCopiedData } from '../copyPaste/copyPasteSlice';
import {
  createDataDependencyEdge,
  createLabwareDependencyEdge,
  deleteEdges,
} from '../graph/graphSlice';
import {
  selectAllUniqueNodePositions,
  selectDataDependencyEdges,
  selectGraphologyGraphWithTaskAndLabwareAndDataDependencies,
  selectLabwareDependencyEdges,
  selectPositionForNewTaskNode,
  selectSelectedEdges,
  selectSelectedNodes,
} from '../graph/selectors';
import { getUniqueId } from '../helpers';
import { selectFlowPositionCenterAfterSidebarOpen } from '../selectors';
import { selectFlowPositionCenter } from '../ui/selectors';
import {
  selectDataExportOutputConnectorsById,
  selectInstrumentDataById,
  selectScripts,
  selectSelectedWorkcellId,
  selectTaskPropertiesStrict,
} from '../workflow/selectors';
import {
  setNumberOfBatches,
  setParameterValues,
  unbindParameterDefinitionOfTask,
} from '../workflow/workflowSlice';
import {
  WorkflowSerializationError,
  selectFieldErrorsForTasksUsingParams,
  selectWorkflowJsonToSave,
} from '../workflowJsonSelectors';

export const thunkCreateLabwareDependencyEdge =
  (connection: Connection, enqueueSnackbar: EnqueueSnackbar): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const edges = selectLabwareDependencyEdges(state);
    const graph =
      selectGraphologyGraphWithTaskAndLabwareAndDataDependencies(state);
    const { source, target, sourceHandle, targetHandle } = connection;

    if (source === null || target === null) {
      logger.error(
        'cannot create edge, bad Connection (null source and/or target):',
        connection
      );

      return;
    }

    const wouldCreateCycle = willCreateCycle(graph, source, target);
    if (wouldCreateCycle) {
      logger.error(
        'aborting connection to prevent unexpected circular dependency',
        connection
      );
      enqueueSnackbar('Connection aborted - circular dependency prevented', {
        variant: 'warning',
      });

      return;
    }

    const startLabwareId = getLabwareId(state, source, sourceHandle);
    const deleteEdgeIds = new Set<string>();

    // remove an edge if there already is one sharing the source and sourceHandle
    // (assumes multiple edges cannot be drawn from one handle)
    const sameOrigin = getSameOriginEdge(edges, source, sourceHandle);
    if (sameOrigin) deleteEdgeIds.add(sameOrigin.id);

    if (sameOrigin !== undefined) {
      deleteEdgeIds.add(sameOrigin.id);
    }

    const sameTargetsWithDifferentLabware = getSameTargetsWithDifferentLabware(
      edges,
      target,
      targetHandle,
      startLabwareId,
      state
    );
    sameTargetsWithDifferentLabware.forEach((e) => deleteEdgeIds.add(e.id));

    const existingLabwareConnection = startLabwareId
      ? edges.find(
          (e) =>
            getLabwareId(state, e.source, e.sourceHandle) === startLabwareId &&
            e.target === target
        )
      : undefined;

    const labwareId = undefined;
    const targetSlot = undefined;

    const edge: Edge = {
      id: getUniqueId({
        type: 'labwareDependencyEdge',
        message: `from-${source}-to-${target}`,
      }),
      source,
      target,
      sourceHandle,
      targetHandle: existingLabwareConnection?.targetHandle ?? targetHandle,
    };
    const payload = {
      edge,
      deleteEdgeIds: Array.from(deleteEdgeIds),
      labwareId,
      targetSlot,
    };

    dispatch(createLabwareDependencyEdge(payload));
    // Create new state after adding the edge to get new edges in the state
    if (startLabwareId) {
      const newState = getState();
      const deleteConflictEdgeIds = getDeleteConflictEdgeIds(
        newState,
        startLabwareId
      );
      if (deleteConflictEdgeIds.length > 0) {
        dispatch(deleteEdges(deleteConflictEdgeIds));
      }
    }
  };

// TODO: Need to remove duplication
export const thunkCreateDataDependencyEdge =
  (connection: Connection, oldEdge?: Edge): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const edges = selectDataDependencyEdges(state);
    const graph =
      selectGraphologyGraphWithTaskAndLabwareAndDataDependencies(state);

    const { source, target, sourceHandle, targetHandle } = connection;

    if (source === null || target === null) {
      logger.error(
        'cannot create edge, bad Connection (null source and/or target):',
        connection
      );

      return;
    }

    const wouldCreateCycle = willCreateCycle(graph, source, target);

    if (wouldCreateCycle) {
      // NOTE(IL, 2024-09-17): this should be caught upstream by isValidConnection ReactFlow callback,
      // this double-check is left to ensure that if some edge case allows a user to get through isValidConnection,
      // we don't allow an invalid non-DAG graph.
      logger.error(
        'aborting connection to prevent unexpected circular dependency',
        connection
      );
      return;
    }

    const deleteEdgeIds: Set<string> = new Set();

    if (oldEdge !== undefined) deleteEdgeIds.add(oldEdge.id);

    // Remove an edge to the target that this new edge will take.
    // (assumes multiple edges cannot connect to one target handle)
    const sameTarget = edges.find(
      (e) => e.target === target && e.targetHandle === targetHandle
    );

    if (sameTarget !== undefined) {
      deleteEdgeIds.add(sameTarget.id);
    }

    const edge: Edge = {
      id: getUniqueId({
        type: 'dataDependencyEdge',
        message: `from-${source}-to-${target}`,
      }),
      source,
      target,
      sourceHandle,
      targetHandle,
    };
    const payload = {
      edge,
      deleteEdgeIds: Array.from(deleteEdgeIds),
    };

    dispatch(createDataDependencyEdge(payload));

    // Unbind when target is a condition task
    const targetProps = selectTaskPropertiesStrict(getState())[target];
    if (targetProps?.propertiesType === PropertiesType.Conditional) {
      // If the target is a condition task, and a data dependency edge is added to it, unbind the parameter definition of the sourceValue field
      dispatch(
        unbindParameterDefinitionOfTask({
          fieldName: 'sourceValue',
          taskId: target,
        })
      );
    }
  };

export const createLabware = createAction<CreateLabwareData>(
  'labware/createLabware'
);

export const createLabwareFromLabwareType =
  (args: { labwareType: LabwareType }): AppThunk =>
  (dispatch, getState) => {
    const { labwareType } = args;
    const state = getState();
    const flowPositionCenter = selectFlowPositionCenterAfterSidebarOpen(state);

    const properties: LabwareProperties = defaultLabwareProperties({
      id: getUniqueId({ type: 'labware', message: labwareType }),
      labwareType,
    });

    const position = determineNewNodePosition(
      flowPositionCenter,
      selectAllUniqueNodePositions(state)
    );

    dispatch(createLabware({ properties, position }));
  };

export const createTask = createAction<CreateTaskData>('task/createTask');

export const thunkCreateTask =
  (
    { taskType }: { taskType: TaskType },
    enqueueSnackbar?: EnqueueSnackbar
  ): AppThunk =>
  (dispatch, getState) => {
    const state = getState();

    const defaultPosition =
      selectPositionForNewTaskNode(state) ??
      selectFlowPositionCenterAfterSidebarOpen(state);
    const position = determineNewNodePosition(
      defaultPosition,
      selectAllUniqueNodePositions(state)
    );
    const id = getUniqueId({ type: 'task', message: taskType });

    if (taskType === 'processdata') {
      const scripts = selectScripts(state);
      const workcellId = selectSelectedWorkcellId(state);

      const scriptFunction = scripts.functions[0];

      if (!scriptFunction) {
        return;
      }

      const scriptName = scriptFunction.function_name;
      const inputDataMode = isFileUploadEnabled(scriptName, workcellId)
        ? DataMode.Manual
        : DataMode.Connector;

      const properties: CodeTaskProperties = {
        id,
        inputDataMode,
        nickname: 'Process data',
        taskType,
        propertiesType: PropertiesType.Code,
        scriptName,
      };
      dispatch(createTask({ properties, position }));

      return;
    }

    if (taskType === 'exportdata') {
      const connectors = selectDataExportOutputConnectorsById(state);
      if (connectors.length === 0) {
        return;
      }

      const properties: DataExportTaskProperties = {
        id,
        nickname: 'Export data',
        taskType,
        propertiesType: PropertiesType.DataExport,
        connectorType: connectors.length === 1 ? connectors[0]?.id : undefined,
      };
      dispatch(createTask({ properties, position }));
      return;
    }

    if (taskType === PropertiesType.Conditional) {
      const properties: ConditionalTaskProperties = {
        elseLabel: 'ELSE Condition',
        id,
        ifLabel: 'IF Condition',
        nickname: 'Condition',
        propertiesType: PropertiesType.Conditional,
        comparison: {
          lhs: { key: '' },
          operator: '==',
          rhs: { value: '' },
        },
        taskType,
        taskAppliedParameters: null,
        expectedOutcome: 'IF condition',
      };
      dispatch(createTask({ properties, position }));
      return;
    }

    if (taskType === PropertiesType.ContinuousLoading) {
      const allTaskProperties = selectTaskPropertiesStrict(state);
      const hasContinuousLoadingNode = Object.values(allTaskProperties).some(
        (task) => task.propertiesType === PropertiesType.ContinuousLoading
      );

      if (hasContinuousLoadingNode) {
        enqueueSnackbar?.(
          'Only one Continuous Loading node is allowed per workflow',
          {
            variant: 'warning',
          }
        );
        return;
      }

      const properties: ContinuousLoadingProperties = {
        id,
        nickname: 'Continuous Loading',
        propertiesType: PropertiesType.ContinuousLoading,
        taskType,
      };
      dispatch(createTask({ properties, position }));
      return;
    }

    const instruments = selectInstrumentDataById(state);

    // TODO: selector for this by task type
    // TODO IMMEDIATELY: test
    // TODO: should sort!
    const defaultInstrument = Object.values(instruments).find((instrument) =>
      instrument.capabilities.includes(taskType)
    );
    const liquidHandlerConfig = getInitialDataModesForTaskType(taskType);

    const tasksWithEmptyDefault = ['store', 'incubate'];
    const shouldHaveEmptyDefault = tasksWithEmptyDefault.includes(taskType);

    const properties: TaskProperties = {
      ...liquidHandlerConfig,
      id,
      nickname: startCase(taskType),
      userTimeEstimate: null,
      taskType,
      instrumentId:
        shouldHaveEmptyDefault || !defaultInstrument
          ? null
          : defaultInstrument.id,
      taskParameters: null, // TODO: populate with default values
      propertiesType: PropertiesType.Action,
      labwareIdsToUnitsConsumed: {},
      isLabwareLessTask: isStartOrStopShakeTask(taskType),
    };

    dispatch(createTask({ properties, position }));
  };

export const createNodesAndEdges = createAction<{
  taskNodes: CreateTaskData[];
  labwareNodes: CreateLabwareData[];
  edges: GraphEdges;
  dataDependencyEdges: GraphEdges;
  timeConstraints: TimeConstraintInstance[];
}>('command/createNodesAndEdges');

export const deleteSelectedNodes = createAction<{}>(
  'command/deleteSelectedNodes'
);

export const deleteNodesEdges = createAction<{
  labwareIds?: string[];
  taskIds?: string[];
  edgeIds?: string[];
}>('command/deleteNodesEdges');

export const thunkDeleteSelectedNodesEdges =
  (): AppThunk => (dispatch, getState) => {
    const state = getState();
    const selectedNodes = selectSelectedNodes(state);
    const selectedEdges = selectSelectedEdges(state);

    const labwareIds = selectedNodes.flatMap((node) =>
      node.nodeType === 'labware' ? [node.id] : []
    );
    const taskIds = selectedNodes.flatMap((node) =>
      node.nodeType === 'task' ? [node.id] : []
    );
    const edgeIds = selectedEdges;

    dispatch(deleteNodesEdges({ taskIds, labwareIds, edgeIds }));
  };

export const keyCommandCut = (): AppThunk => (dispatch, getState) => {
  handleCutCopyData(dispatch, getState, 'cut');
};

export const keyCommandCopy = (): AppThunk => (dispatch, getState) => {
  handleCutCopyData(dispatch, getState, 'copy');
};

export const keyCommandPaste = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const copiedData = selectCopiedData(state);

  const positionForNewTaskNode = selectPositionForNewTaskNode(state);
  const defaultPosition = determineNewNodePosition(
    selectFlowPositionCenterAfterSidebarOpen(state),
    selectAllUniqueNodePositions(state)
  );

  if (copiedData !== null) {
    const { dataType } = copiedData;

    if (dataType === 'task') {
      const { properties, comments } = copiedData.data;
      const position = positionForNewTaskNode ?? defaultPosition;

      dispatch(
        createTask({
          properties: {
            ...properties,
            id: getUniqueId({ type: 'task', message: properties.taskType }),
          },
          position,
          comments,
        })
      );
    } else if (dataType === 'labware') {
      const { properties, colorIndex, comments } = copiedData.data;
      const position = defaultPosition;

      dispatch(
        createLabware({
          properties: {
            ...properties,
            id: getUniqueId({
              type: 'labware',
              message: properties.labwareType,
            }),
          },
          position,
          colorIndex,
          comments,
        })
      );
    } else if (dataType === 'multi') {
      const copiedIdToNewIdMapping: Record<string, string> = {};

      // NOTE(IL, 2024-04-19): pasting will close sidebar if open, so we must account for the repositioning
      const positionCenter = selectFlowPositionCenter(state);

      const taskNodes: CreateTaskData[] = [];
      for (const task of copiedData.taskNodes) {
        const copiedId = task.properties.id;
        const newId = getUniqueId({
          type: 'task',
          message: task.properties.taskType,
        });
        taskNodes.push({
          ...task,
          properties: {
            ...task.properties,
            id: newId,
          },
          position: addPositions(task.position, positionCenter),
        });
        copiedIdToNewIdMapping[copiedId] = newId;
      }

      const labwareNodes: CreateLabwareData[] = [];
      for (const labware of copiedData.labwareNodes) {
        const copiedId = labware.properties.id;
        const newId = getUniqueId({
          type: 'labware',
          message: labware.properties.labwareType,
        });
        labwareNodes.push({
          ...labware,
          properties: { ...labware.properties, id: newId },
          position: addPositions(labware.position, positionCenter),
        });
        copiedIdToNewIdMapping[copiedId] = newId;
      }

      const edges: GraphEdges = [];
      for (const copiedEdge of copiedData.edges) {
        const source = copiedIdToNewIdMapping[copiedEdge.source];
        const target = copiedIdToNewIdMapping[copiedEdge.target];

        if (source === undefined || target === undefined) {
          logger.error(
            `Tried to copy edge ${copiedEdge.id}, no mapping for source or target`,
            { source, target }
          );
          continue;
        }

        // NOTE(IL, 2024-04-19): not ensuring handle IDs are globally unique, we'll copy them exactly.
        const { sourceHandle, targetHandle } = copiedEdge;
        const newEdge: Edge = {
          id: getUniqueId({
            type: 'edge',
            message: `from-${source}-to-${target}`,
          }),
          source,
          target,
          sourceHandle,
          targetHandle,
        };
        edges.push(newEdge);
      }

      const dataDependencyEdges: GraphEdges = [];
      for (const copiedEdge of copiedData.dataDependencyEdges) {
        const source = copiedIdToNewIdMapping[copiedEdge.source];
        const target = copiedIdToNewIdMapping[copiedEdge.target];

        if (source === undefined || target === undefined) {
          logger.error(
            `Tried to copy edge ${copiedEdge.id}, no mapping for source or target`,
            { source, target }
          );
          continue;
        }

        const { sourceHandle, targetHandle } = copiedEdge;
        const newEdge: Edge = {
          id: getUniqueId({
            type: 'dataDependencyEdge',
            message: `from-${source}-to-${target}`,
          }),
          source,
          target,
          sourceHandle: sourceHandle?.replace(copiedEdge.source, source),
          targetHandle: targetHandle?.replace(copiedEdge.target, target),
        };
        dataDependencyEdges.push(newEdge);
      }

      const timeConstraints: TimeConstraintInstance[] = [];
      for (const timeConstraint of copiedData.timeConstraints) {
        const source = copiedIdToNewIdMapping[timeConstraint.source];
        const target = copiedIdToNewIdMapping[timeConstraint.target];

        if (source === undefined || target === undefined) {
          // eslint-disable-next-line no-console
          console.error(
            `Tried to copy edge ${timeConstraint.id}, no mapping for source or target`,
            { source, target }
          );
        } else {
          const {
            minDuration,
            maxDuration,
            sourceType,
            targetType,
            enableDetailedCoverageCalculation,
            autoIgnoreIfBroken,
          } = timeConstraint;

          const newTimeConstraint: TimeConstraintInstance = {
            id: getUniqueId({
              type: 'timeConstraint',
              message: `from-${source}-to-${target}`,
            }),
            source,
            sourceType,
            target,
            targetType,
            minDuration,
            maxDuration,
            enableDetailedCoverageCalculation,
            autoIgnoreIfBroken,
          };
          timeConstraints.push(newTimeConstraint);
        }
      }

      dispatch(
        createNodesAndEdges({
          taskNodes,
          labwareNodes,
          edges,
          timeConstraints,
          dataDependencyEdges,
        })
      );
    } else {
      dataType satisfies never;
    }
  }
};

export const cutNodeToClipboard = createAction<CutData>(
  'command/cutNodeToClipboard'
);

export const loadScripts = createAction<OrgScriptsInfo>('workflow/loadScripts');

export const setBatchesForSavingWorkflow =
  ({ batches }: { batches: number }): AppThunk<WorkflowConfigInput> =>
  (dispatch, getState) => {
    dispatch(setNumberOfBatches({ batches }));

    return selectWorkflowJsonToSave(getState());
  };

export const getFieldErrorsForTasks =
  (parameters: ParameterDefinition[]): AppThunk<WorkflowSerializationError[]> =>
  (dispatch, getState) => {
    dispatch(setParameterValues(parameters));

    return selectFieldErrorsForTasksUsingParams(getState());
  };
